import type { Money } from '@fint/money';
import type { OverviewCharts, OverviewChartsBook, OverviewChartsMonth } from '@fint/validation';

/**
 * The Overview's month-by-month table, as plain functions from the charts
 * response (and each book's own targets) to rows.
 *
 * ── WHAT IT IS ──
 * The per-book dashboard's "Month by month" table - money in, money out,
 * profit, the running total, the goal - for every book at once, NET of money
 * that moved between them, with each book's own months one tap away. The
 * owner's spreadsheet had this shape per book; this is the sheet that sums
 * the sheets.
 *
 * ── WHERE THE MONEY ARITHMETIC COMES FROM ──
 * Every figure here goes through `@fint/money`, and never through a float.
 * The functions are HANDED IN rather than imported: `@fint/money`'s ESM
 * build has extensionless relative imports, which Vite resolves and Node's
 * test runner does not (the same reason `overview-charts.ts` takes a `minor`
 * function and `tokens.test.ts` reaches for `createRequire`). The component
 * passes the real package; the test loads the CJS build by hand and passes
 * the same functions. Either way the maths is the package's, not a copy.
 *
 * ── THE CONTRACT ──
 * GET /v1/practice/overview/charts gives every book's months and the net
 * series across the counted books; GET /v1/targets, under each counted
 * book's own org id, gives that book's monthly income targets. A withheld
 * book has NULL months and a caveat naming it; it is listed with dashes,
 * never as zeros - "you may not see this" and "nothing happened" are
 * different facts.
 */

/** The `@fint/money` functions the table uses, in the package's own types. */
export type MoneyKit = Pick<typeof import('@fint/money'), 'parse' | 'add' | 'subtract' | 'divide' | 'format' | 'zero'>;

/** One row of `GET /v1/targets` for one book. `periodStart` is `YYYY-MM-01`. */
export interface BookTarget {
  orgId: string;
  periodStart: string;
  amount: string;
  currency: string;
}

/** What one book did in one month, before any elimination. Null figures mean the book is withheld. */
export interface BookMonthRow {
  orgId: string;
  name: string;
  colour: string;
  currency: string;
  withheld: boolean;
  /** Whether this book is inside the net row: figured, and in the table's currency. */
  inTotals: boolean;
  /** The caveat naming this book, for a withheld one; the other-currency note otherwise. */
  note: string | null;
  income: Money | null;
  expense: Money | null;
  profit: Money | null;
  /** Of this book's month, what came IN from another of the person's books. */
  betweenIn: Money | null;
  /** Of this book's month, what went OUT to another of the person's books. */
  betweenOut: Money | null;
  /** This book's own income target for the month, when it set one. */
  target: Money | null;
  /** This book's own income less its target: what its own dashboard says. */
  vsTarget: Money | null;
}

/** One month across the counted books, net of money between them. */
export interface MonthRow {
  /** `YYYY-MM`. */
  month: string;
  income: Money;
  expense: Money;
  profit: Money;
  /** Cumulative profit from the first month of the period. */
  running: Money;
  /** The counted books' targets for the month added up, or null when none set one. */
  goal: Money | null;
  /** `income − goal`, or null when there is no goal. */
  vsGoal: Money | null;
  /** What the elimination took out of this month - the larger half, since a transfer is one amount. */
  between: Money;
  /** Whether the net row moved at all: income or expense non-zero. The averages divide by these. */
  active: boolean;
  /** Every book, in the response's order: counted ones with figures, withheld ones with nulls. */
  books: BookMonthRow[];
}

export interface MonthsTable {
  currency: string;
  rows: MonthRow[];
  totals: {
    income: Money;
    expense: Money;
    profit: Money;
    /** The goals added up, or null when no month had one. */
    goal: Money | null;
    /** The monthly `vsGoal` figures added up, over the months that had a goal. */
    vsGoal: Money | null;
    /** How many months the goal covers, so "over 2 of 12 months" can be said. */
    goalMonths: number;
    /** The larger half of what moved between the counted books over the period. */
    between: Money;
  };
  averages: {
    /** The divisor, printed beside the row so it is never a guess. */
    months: number;
    income: Money;
    expense: Money;
    profit: Money;
  };
  /** Whether any month has a goal. When none does, the goal columns go - see the component. */
  hasAnyGoal: boolean;
  /** Whether any counted book moved money in the period. When nothing did, the table is an empty state. */
  anyMovement: boolean;
  /** How many books are inside the net row. */
  countedBooks: number;
}

/** Whether a response has the shape the table reads. A mocked or older body must fall to the error state, not throw mid-render. */
export function isChartsBody(value: unknown): value is OverviewCharts {
  if (!value || typeof value !== 'object') return false;
  const body = value as Partial<OverviewCharts>;
  return (
    Array.isArray(body.books) &&
    Array.isArray(body.months) &&
    typeof body.currency === 'string' &&
    !!body.totals &&
    Array.isArray(body.totals.months) &&
    !!body.totals.betweenBooks &&
    Array.isArray(body.caveats)
  );
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-04` → `Apr 2026`, the dashboard's own spelling. Anything else comes back untouched. */
export function monthName(ym: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!match) return ym;
  const name = MONTH_NAMES[Number(match[2]) - 1];
  return name ? `${name} ${match[1]}` : ym;
}

/** The larger of two amounts. A transfer is one sum recorded twice; the larger half is the sum. */
function larger(a: Money, b: Money): Money {
  return a.minor >= b.minor ? a : b;
}

/**
 * The note under a book's name in a month's expansion: the caveat that names
 * a withheld book, or the currency note for one that keeps its books in
 * another currency. The caveats are sentences; the one about this book is
 * the one that mentions it.
 */
function bookNote(book: OverviewChartsBook, data: OverviewCharts): string | null {
  if (book.withheld) {
    return data.caveats.find((caveat) => caveat.includes(book.name)) ?? 'Figures not shown — see the notes under the table.';
  }
  if (!book.inTotals) return `In ${book.currency}, not added to the row above.`;
  return null;
}

function bookMonth(
  book: OverviewChartsBook,
  month: string,
  data: OverviewCharts,
  targetFor: (orgId: string, month: string, currency: string) => Money | null,
  money: MoneyKit,
): BookMonthRow {
  const base = {
    orgId: book.orgId,
    name: book.name,
    colour: book.colour,
    currency: book.currency,
    withheld: book.withheld,
    inTotals: book.inTotals,
    note: bookNote(book, data),
  };
  if (book.withheld || book.months === null) {
    return { ...base, income: null, expense: null, profit: null, betweenIn: null, betweenOut: null, target: null, vsTarget: null };
  }
  // A month the book has no row for is a month nothing happened, in the
  // book's own currency - the same rule the charts draw by.
  const row: OverviewChartsMonth | undefined = book.months.find((candidate) => candidate.month === month);
  const income = money.parse(row?.income ?? '0', book.currency);
  const expense = money.parse(row?.expense ?? '0', book.currency);
  const target = targetFor(book.orgId, month, book.currency);
  return {
    ...base,
    income,
    expense,
    profit: money.subtract(income, expense),
    betweenIn: money.parse(row?.betweenBooks.income ?? '0', book.currency),
    betweenOut: money.parse(row?.betweenBooks.expense ?? '0', book.currency),
    target,
    vsTarget: target ? money.subtract(income, target) : null,
  };
}

/**
 * The rows, the Total, the Averages.
 *
 * ── THE NET ROW IS THE API'S, RE-ADDED ──
 * Income and expense for a month come from `totals.months`, which the server
 * has already netted of money between books under the same two-or-more rule
 * as the Overview's Total row. Profit and the running total are derived here
 * from those two rather than read back, so every figure on a row is the
 * arithmetic of the ones beside it: income − expense = profit, and the
 * running column is the profits summed from the period's first month. The
 * server sends the same numbers; deriving them is what makes that a
 * property of the screen and not a hope about the wire.
 *
 * ── THE GOAL IS THE BOOKS' GOALS, ADDED ──
 * A target is set inside one book, against that book's money in. The row's
 * goal is the counted books' targets for the month summed, and `vsGoal` is
 * the row's own income less it - so the three columns add up in front of
 * the reader. A month where no counted book set a target has NO goal, not a
 * goal of zero: a zero target makes every month look like a triumph. Each
 * book's own target and variance travel with its line in the expansion, so
 * a person can see which book's goal the row is carrying.
 *
 * ── ACTIVITY IS ABOUT THE NET ROW ──
 * "Had activity" is the dashboard's rule applied to the row in front of the
 * reader: income or expense non-zero. A month whose only movement was a
 * salary from one of the person's books to another nets to nothing and is
 * not an active month of the whole, however busy each book's own table
 * says it was. The averages divide by the active months and print the
 * divisor, because averaging a half-year over twelve months halves every
 * figure and the reader would have no way to know.
 */
export function buildMonthsTable(data: OverviewCharts, targets: BookTarget[], money: MoneyKit): MonthsTable {
  const currency = data.currency;
  const { parse, add, subtract, divide, zero } = money;

  // A target counts only in the currency of the book it was set in: one
  // from before a currency change cannot be added to anything.
  const targetFor = (orgId: string, month: string, bookCurrency: string): Money | null => {
    const found = targets.find(
      (target) => target.orgId === orgId && target.periodStart.slice(0, 7) === month && target.currency === bookCurrency,
    );
    return found ? parse(found.amount, bookCurrency) : null;
  };

  let running = zero(currency);
  const rows: MonthRow[] = data.months.map((month) => {
    const net = data.totals.months.find((candidate) => candidate.month === month);
    const income = parse(net?.income ?? '0', currency);
    const expense = parse(net?.expense ?? '0', currency);
    const profit = subtract(income, expense);
    running = add(running, profit);
    const between = larger(
      parse(net?.betweenBooks.income ?? '0', currency),
      parse(net?.betweenBooks.expense ?? '0', currency),
    );
    const books = data.books.map((book) => bookMonth(book, month, data, targetFor, money));
    const goals = books.filter((book) => book.inTotals).map((book) => book.target).filter((target): target is Money => target !== null);
    const goal = goals.length > 0 ? add(...goals) : null;
    return {
      month,
      income,
      expense,
      profit,
      running,
      goal,
      vsGoal: goal ? subtract(income, goal) : null,
      between,
      active: income.minor !== 0 || expense.minor !== 0,
      books,
    };
  });

  const sum = (pick: (row: MonthRow) => Money | null): Money =>
    rows.reduce((total, row) => {
      const value = pick(row);
      return value ? add(total, value) : total;
    }, zero(currency));
  const goalRows = rows.filter((row) => row.goal !== null);
  const activeRows = rows.filter((row) => row.active);
  const average = (pick: (row: MonthRow) => Money): Money =>
    activeRows.length === 0
      ? zero(currency)
      : divide(activeRows.reduce((total, row) => add(total, pick(row)), zero(currency)), activeRows.length);

  const counted = data.books.filter((book) => book.inTotals && !book.withheld);
  const anyMovement =
    rows.some((row) => row.active) ||
    counted.some((book) => book.months?.some((row) => parse(row.income, book.currency).minor !== 0 || parse(row.expense, book.currency).minor !== 0) ?? false);

  return {
    currency,
    rows,
    totals: {
      income: sum((row) => row.income),
      expense: sum((row) => row.expense),
      profit: sum((row) => row.profit),
      goal: goalRows.length > 0 ? sum((row) => row.goal) : null,
      vsGoal: goalRows.length > 0 ? sum((row) => row.vsGoal) : null,
      goalMonths: goalRows.length,
      between: larger(parse(data.totals.betweenBooks.income, currency), parse(data.totals.betweenBooks.expense, currency)),
    },
    averages: {
      months: activeRows.length,
      income: average((row) => row.income),
      expense: average((row) => row.expense),
      profit: average((row) => row.profit),
    },
    hasAnyGoal: goalRows.length > 0,
    anyMovement,
    countedBooks: counted.length,
  };
}

// ── the spreadsheet view: a column group per book ─────────────────────────

export interface PivotBook {
  orgId: string;
  name: string;
  colour: string;
  currency: string;
  withheld: boolean;
  inTotals: boolean;
  note: string | null;
}

export interface PivotCell {
  income: Money;
  expense: Money;
  profit: Money;
}

export interface PivotRow {
  month: string;
  /** Aligned with `Pivot.books`; null where the book is withheld. */
  cells: Array<PivotCell | null>;
  /** The net row: the counted books together, less money between them. */
  net: PivotCell;
}

export interface Pivot {
  books: PivotBook[];
  rows: PivotRow[];
  totals: { cells: Array<PivotCell | null>; net: PivotCell };
}

/**
 * The same months with the books as column groups - the owner's original
 * spreadsheet, one sheet per book, side by side, with the net beside them.
 * Each book's cells are its OWN figures, so the salary the company paid is
 * in its "out" and in the person's "in"; the Net group is where it is not.
 */
export function pivotByBook(table: MonthsTable, money: MoneyKit): Pivot {
  const first = table.rows[0];
  const books: PivotBook[] = (first?.books ?? []).map((book) => ({
    orgId: book.orgId,
    name: book.name,
    colour: book.colour,
    currency: book.currency,
    withheld: book.withheld,
    inTotals: book.inTotals,
    note: book.note,
  }));

  const rows: PivotRow[] = table.rows.map((row) => ({
    month: row.month,
    cells: row.books.map((book) =>
      book.income && book.expense && book.profit ? { income: book.income, expense: book.expense, profit: book.profit } : null,
    ),
    net: { income: row.income, expense: row.expense, profit: row.profit },
  }));

  const cells = books.map((book, index): PivotCell | null => {
    if (book.withheld) return null;
    const start = money.zero(book.currency);
    const total = rows.reduce(
      (sum, row) => {
        const cell = row.cells[index];
        return cell
          ? { income: money.add(sum.income, cell.income), expense: money.add(sum.expense, cell.expense), profit: money.add(sum.profit, cell.profit) }
          : sum;
      },
      { income: start, expense: start, profit: start },
    );
    return total;
  });

  return {
    books,
    rows,
    totals: { cells, net: { income: table.totals.income, expense: table.totals.expense, profit: table.totals.profit } },
  };
}

// ── the remembered view ───────────────────────────────────────────────────

export type MonthsView = 'net' | 'books';

/** The localStorage key. A person who wanted the spreadsheet view wants it next time too. */
export const VIEW_STORAGE_KEY = 'fint-overview-months-view';

/**
 * The remembered view, or the net table. Storage can be absent, full or
 * refused (a private window, a locked-down browser); none of those is a
 * reason for the table not to render.
 */
export function readView(storage: { getItem(key: string): string | null } | null | undefined): MonthsView {
  try {
    return storage?.getItem(VIEW_STORAGE_KEY) === 'books' ? 'books' : 'net';
  } catch {
    return 'net';
  }
}

export function rememberView(storage: { setItem(key: string, value: string): void } | null | undefined, view: MonthsView): void {
  try {
    storage?.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Refused or full: the choice lives for the session and is asked again next time.
  }
}
